/**
 * Bulk registration — pick several events for one team.
 *
 * Lists the embed's events this team isn't registered for yet
 * (GET /teams/{id}/available-events, already scoped and de-duplicated
 * server-side). Selection is held in FlowContext so it survives the
 * per-route remount.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { EmbedClient, AvailableEvent, BatchPreview, BulkOffer } from '@/api/embed';
import { useFeature } from '@/flags';
import { useFlow } from '@/state/FlowContext';
import { money, shortDate } from './bulkFormat';
import { bestOffer, nextOffer, offerDiscount, offerLabel, pctOff, plural } from './bulkOffers';
import './Bulk.css';

interface Props {
  client: EmbedClient;
}

export function BulkEventsPage({ client }: Props) {
  const navigate = useNavigate();
  const { selectedTeam, bulkEvents, bulkDivisions, bulkCarriedId, setBulkEvents } = useFlow();
  const [events, setEvents] = useState<AvailableEvent[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(bulkEvents.map((e) => e.id)));
  const [error, setError] = useState<string | null>(null);
  const strip = useFeature('bulk_discount_strip');
  // null until the organizer's tiers are known (a failed lookup counts as
  // none), so we can tell "no multi-event discount" from "not loaded yet".
  const [offers, setOffers] = useState<BulkOffer[] | null>(null);
  // data is null when the check for that selection failed (it then doesn't
  // hold the coach up; review prices it again).
  const [preview, setPreview] = useState<{ key: string; data: BatchPreview | null } | null>(null);

  useEffect(() => {
    if (!selectedTeam) {
      navigate('/bulk/teams', { replace: true });
      return;
    }
    client
      .getAvailableEvents(selectedTeam.id)
      .then(setEvents)
      .catch((err) => {
        if (err?.response?.status === 401) {
          navigate('/bulk/auth', { replace: true });
          return;
        }
        setError('Could not load events. Please try again.');
      });
  }, [client, selectedTeam, navigate]);

  // Multi-event tiers. Optional: on any failure the list just shows fees.
  useEffect(() => {
    if (!strip || !selectedTeam) return;
    client.getBulkOffers().then(setOffers).catch(() => setOffers([]));
  }, [client, strip, selectedTeam]);

  // Live prices for the current selection, from the same preview checkout
  // uses (it already applies whichever discount is greater per event).
  const pickedKey = useMemo(() => [...picked].sort().join(','), [picked]);
  useEffect(() => {
    if (!strip || !selectedTeam || !pickedKey) return;
    let stale = false;
    const t = window.setTimeout(() => {
      client
        .previewBatch(selectedTeam.id, pickedKey.split(',').map((event_id) => ({ event_id })))
        .then((data) => { if (!stale) setPreview({ key: pickedKey, data }); })
        .catch(() => { if (!stale) setPreview({ key: pickedKey, data: null }); });
    }, 250);
    return () => { stale = true; window.clearTimeout(t); };
  }, [client, strip, selectedTeam, pickedKey]);

  if (!selectedTeam) return null;
  if (error) return <div role="alert" className="tn-bulk__error">{error}</div>;
  if (!events) return <div className="tn-bulk__loading">Loading events…</div>;

  if (events.length === 0) {
    return (
      <div className="tn-card tn-bulk__empty">
        <h2>Nothing left to register</h2>
        <p>{selectedTeam.name} is already registered for every open event here.</p>
      </div>
    );
  }

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const count = picked.size;
  // Only a preview of exactly this selection prices the rows and button.
  const quote = preview && preview.key === pickedKey && count > 0 ? preview.data : null;
  // Until the organizer's tiers have loaded we can't tell whether there's a
  // multi-event discount: say so and hold Continue. After that, pricing is
  // instant.
  const checking = strip && count > 0 && offers === null;
  const pickedEvents = events.filter((e) => picked.has(e.id));
  // Prices come straight from the tiers as the coach picks. The server's
  // preview, once it lands for this exact selection, refines them: a team's
  // own discount (loyalty, a standing coupon) that beats the tier, and
  // account credit.
  const estimate = (e: AvailableEvent) => {
    const fee = priceRange(e).min;
    const tier = offers ? bestOffer(offers, count, fee) : null;
    return { fee, discount: tier ? offerDiscount(tier, fee) : 0 };
  };
  const discountFor = (e: AvailableEvent) =>
    quote?.items.find((i) => i.event_id === e.id)?.discount_cents ?? estimate(e).discount;
  const totals = quote
    ? { discount: quote.discount_cents, total: quote.total_cents }
    : pickedEvents.reduce(
        (t, e) => {
          const { fee, discount } = estimate(e);
          return { discount: t.discount + discount, total: t.total + fee - discount };
        },
        { discount: 0, total: 0 },
      );
  const offerNode = !strip ? null : checking ? (
    <div className="tn-bulk__offer tn-bulk__offer--checking" role="status">
      <span className="tn-bulk__offer-head">Checking your multi-event discount…</span>
    </div>
  ) : offerStrip(offers ?? [], events, pickedEvents, quote);
  const pickedVaries = pickedEvents.some((e) => priceRange(e).varies);

  const onContinue = () => {
    setBulkEvents(events.filter((e) => picked.has(e.id)));
    navigate('/bulk/divisions');
  };

  return (
    <div className="tn-bulk">
      <h1 className="tn-bulk__title">{bulkCarriedId ? 'Add more events' : 'Choose events'}</h1>
      <p className="tn-bulk__subtitle">Events {selectedTeam.name} isn&rsquo;t registered for yet.</p>

      {offerNode}

      <ul className="tn-bulk__group" role="group" aria-label="Events">
        {events.map((e) => {
          const on = picked.has(e.id);
          const carried = e.id === bulkCarriedId;
          const meta = carried
            ? [bulkDivisions[e.id], 'from this registration'].filter(Boolean).join(' · ')
            : [shortDate(e.event_date, e.ends_at), e.venue_name || e.location].filter(Boolean).join(' · ');
          return (
            <li key={e.id}>
              <label className={`tn-bulk__row${on ? ' tn-bulk__row--on' : ''}${on && checking ? ' tn-bulk__row--checking' : ''}`}>
                <input
                  type="checkbox"
                  className="tn-bulk__check"
                  checked={on}
                  onChange={() => toggle(e.id)}
                />
                <span className="tn-bulk__row-text">
                  <span className="tn-bulk__row-title">{e.name}</span>
                  {meta ? (
                    <span className={`tn-bulk__row-meta${carried ? ' tn-bulk__row-meta--carried' : ''}`}>{meta}</span>
                  ) : null}
                  {e.registration_mode === 'approval_required' ? (
                    <span className="tn-bulk__row-meta">Needs organizer approval before payment</span>
                  ) : null}
                </span>
                {priceRange(e).max > 0 ? (
                  <RowPrice event={e} discount={on && !checking ? discountFor(e) : 0} />
                ) : null}
              </label>
            </li>
          );
        })}
      </ul>

      <div className="tn-bulk__actions">
        {/* A picked event whose price depends on the division has no exact
            total yet; the review step shows it once divisions are chosen. */}
        {pickedVaries ? (
          <p className="tn-bulk__fine">Prices depend on the division you choose next.</p>
        ) : !checking && totals.discount > 0 ? (
          <p className="tn-bulk__save">You save <b>{money(totals.discount)}</b></p>
        ) : null}
        <button type="button" className="tn-btn tn-btn--primary" disabled={count === 0 || checking} onClick={onContinue}>
          {count === 0
            ? 'Choose at least one event'
            : checking
            ? 'Checking discount…'
            : `Continue with ${plural(count, 'event')}${!pickedVaries && totals.total > 0 ? ` · ${money(totals.total)}` : ''}`}
        </button>
      </div>
    </div>
  );
}

/** An event's price across its divisions (older APIs send only fee_cents).
 *  The event's own fee can be 0 when the organizer prices divisions. */
function priceRange(e: AvailableEvent) {
  const min = e.fee_min_cents ?? e.fee_cents;
  const max = e.fee_max_cents ?? e.fee_cents;
  return { min, max, varies: max > min };
}

/** "$175+" for an event whose division prices differ. */
function fromPrice(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}+`;
}

function RowPrice({ event, discount }: { event: AvailableEvent; discount: number }) {
  const range = priceRange(event);
  if (range.varies) {
    // The exact price waits for the division; the percent off doesn't.
    const fee = range.min;
    return (
      <span className="tn-bulk__row-price tn-bulk__price tn-bulk__price--from">
        <span className="tn-bulk__price-now">{fromPrice(range.min)}</span>
        {discount > 0 && fee > 0 ? <span className="tn-bulk__off">&minus;{pctOff(discount, fee)}%</span> : null}
      </span>
    );
  }
  // One price for every division: that price (the event's own fee may be
  // unset when the organizer prices divisions instead).
  const fee = range.min;
  if (discount <= 0) return <span className="tn-bulk__row-price">{money(fee)}</span>;
  return (
    <span className="tn-bulk__row-price tn-bulk__price">
      <span className="tn-bulk__price-was">{money(fee)}</span>
      <span className="tn-bulk__price-now">{money(fee - discount)}</span>
      <span className="tn-bulk__off">&minus;{pctOff(discount, fee)}%</span>
    </span>
  );
}

/** The line above the list: the tiers up front, then what's applied and
 *  what the next tier needs. Nothing when no tier is reachable here. */
function offerStrip(
  offers: BulkOffer[],
  events: AvailableEvent[],
  picked: AvailableEvent[],
  quote: BatchPreview | null,
) {
  const reachable = offers.filter((o) => o.min_events <= events.length);
  if (reachable.length === 0) return null;
  const pool = picked.length ? picked : events;
  const fee = Math.round(pool.reduce((sum, e) => sum + priceRange(e).min, 0) / pool.length);
  const n = picked.length;
  const current = bestOffer(reachable, n, fee);
  const next = nextOffer(reachable, n, fee, current);

  if (!current) {
    const first = next ?? reachable[0];
    if (first.min_events - n <= 0) return null; // the tier already applies
    return (
      <div className="tn-bulk__offer">
        <span className="tn-bulk__offer-head">
          Register for {plural(first.min_events, 'event')}: save {offerLabel(first)} on each
        </span>
        <span className="tn-bulk__offer-tiers">
          {reachable.map((o, i) => (
            <span key={`${o.min_events}-${i}`}>
              {offerLabel(o)} off {o.min_events}+{i === 0 ? ' events' : ''}
            </span>
          ))}
        </span>
      </div>
    );
  }

  // The team's own discount (loyalty, standing coupon) beats the tier on
  // every picked event: say so rather than claim the tier applies.
  const lines = quote?.items ?? [];
  const ownWins =
    lines.length > 0 &&
    lines.every((l) => l.discount_cents > offerDiscount(current, l.fee_cents));
  if (ownWins) {
    const pcts = new Set(lines.map((l) => pctOff(l.discount_cents, l.fee_cents)));
    const own = pcts.size === 1 ? `${[...pcts][0]}% ` : '';
    return (
      <div className="tn-bulk__offer tn-bulk__offer--on">
        <span className="tn-bulk__offer-head">Your {own}team discount applies</span>
        <span className="tn-bulk__offer-next">Better than the {offerLabel(current)} multi-event discount</span>
      </div>
    );
  }

  return (
    <div className="tn-bulk__offer tn-bulk__offer--on">
      {next ? (
        <>
          <span className="tn-bulk__offer-head">{offerLabel(current)} multi-event discount applied</span>
          <span className="tn-bulk__offer-next">
            Register for {plural(next.min_events, 'event')}: save {offerLabel(next)} on each
          </span>
        </>
      ) : (
        <span className="tn-bulk__offer-head">{offerLabel(current)} off, the best multi-event rate</span>
      )}
    </div>
  );
}
